import { useState } from 'react'
import { CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react'
import { fieldsBySource } from 'app/lib/workflows/dataContext'
import type { PreviewSample } from '../usePreviewSample'
import { Button } from 'app/components/ui/button'
import { InfoHint } from './fields'
import { SECTION_TITLE_CLASS } from './Section'

const truncate = (value: string, max = 18) => (value.length > max ? `${value.slice(0, max - 1)}…` : value)

/** The data-context footer on trigger/step drawers: which fields are in scope here, so
 *  every step can answer "what can I reference here?". Collapsed by default — the per-field
 *  "Insert variable" picker covers the everyday need, so the full roster is reference material,
 *  not something worth a permanent wall of chips in every drawer. Expanding reveals the grouped
 *  fields and (when a `preview` sample is supplied) the value each resolves to for a real record,
 *  plus the record picker that drives every preview in the builder. */
export function AvailableData({
  fields,
  title,
  note,
  preview,
}: {
  fields: string[]
  title: string
  note?: string
  preview?: PreviewSample
}) {
  const [open, setOpen] = useState(false)
  const groups = fieldsBySource(fields)
  if (groups.length === 0) return null
  const sample = preview?.sample
  const count = groups.reduce((n, g) => n + g.fields.length, 0)
  return (
    <div>
      <Button
        variant="subtle"
        size="inline"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full justify-start"
      >
        {open ? (
          <CaretDownIcon size={10} weight="bold" className="size-2.5 text-minimal" />
        ) : (
          <CaretRightIcon size={10} weight="bold" className="size-2.5 text-minimal" />
        )}
        <span className={SECTION_TITLE_CLASS}>{title}</span>
        <span className="text-[11px] text-minimal">· {count} fields</span>
        {/* A scope note ("contact fields need Require a matched contact") sits behind the "i" so the
            row stays one line, and it is on the header so the collapse never hides it. */}
        {note && <InfoHint text={note} />}
      </Button>
      {open && (
        <div className="space-y-3 pt-3">
          {/* Pick which real record the whole builder previews against. */}
          {preview && preview.samples.length > 0 && (
            <div className="flex justify-end">
              <select
                value={preview.selectedId ?? ``}
                onChange={(e) => preview.select(e.target.value)}
                title="Preview these fields with a real recent record"
                className="max-w-[55%] truncate rounded border border-default bg-surface px-1.5 py-0.5 text-[10px] text-subtle focus:border-blue-500 focus:outline-none"
              >
                {preview.samples.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-2">
            {groups.map((group) => (
              <div key={group.source}>
                <div className="mb-1 text-[9px] font-semibold tracking-wide text-minimal uppercase">
                  {group.label} fields
                </div>
                <div className="flex flex-wrap gap-1">
                  {group.fields.map((field) => {
                    const value = sample?.[field.key]
                    return (
                      <span
                        key={field.key}
                        title={value ? `${field.label} → ${value}` : field.label}
                        className="inline-flex items-center gap-1 rounded bg-surface px-1.5 py-0.5 text-[10px] font-medium text-subtle ring-1 ring-subtle"
                      >
                        {field.label}
                        {value && <span className="text-minimal">{truncate(value)}</span>}
                      </span>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
